import * as React from 'react';
import { Link } from 'react-router';
import styled, { withTheme } from 'styled-components';
import { rem, darken } from 'polished';
import {
  getProductDefaultInfo,
  getCartProductAvailabilityInfo,
  getCartProductInfo,
} from '../../../../utilities/selectors';
import {
  getZemplinProductDefaultInfo,
  getZemplinCartProductInfo,
  getZemplinTrueCount,
  getPackagesString,
  getCountSetterColor,
  showWholePackagesOnlyWarning,
} from '../../selectors';
import { OrderNrInput } from '../../Product/OrderNrInput';
import { StockInfo } from '../../Product/StockInfo';
import { ProductPriceCartTable } from '../../Product/ProductPriceCartTable';
import { TableTd, TableRow } from '../../General/Table';
import {
  FlexCol,
  FlexRowCenterHorizontal,
  FlexRow,
  FlexRowCenterVertical,
} from '../../../Defaults/Flexbox/Flexbox';
import { StyledLink, TextP } from '../../../Defaults/Text';
import { IconType, SvgIcon } from '../../General/SvgIcon';
import { CountSetter } from '../../ProductDetail/CountSetter';
import { FormInput } from '../../Form/FormInput';
import { DotDelimeter } from '../../General/DotDelimeter';
import { CustomNrStock } from './CustomNrStock';
import { __, __r } from 'react-i18n';
import { MobileTitleValue } from './MobileTitleValue';
import {
  getSymbolFromCurrency,
  formatPriceToString,
  round,
  prop,
} from '../../../../utilities';
import { MinAmountInfoText } from './MinAmountInfoText';
import { WholePackagesOnly } from './WholePackageOnlyInfoText';
import { AlreadyOrderedText } from './ItemsTabRow';
import { Tag, TagType } from '../../Product/Tag';
import { SaleBubbleNew } from '../../ProductDetail/SaleBubbleNew';

export interface Props {
  product: any;
  addToCart: any;
  removeFromCart: any;
  theme: any;
  hasMinAmountInfo?: boolean;
  isB2B: boolean;
  wholePackagesOnly: boolean;
}

function ItemsTabRowMobile({
  product,
  addToCart,
  theme,
  hasMinAmountInfo,
  removeFromCart,
  isB2B,
}: Props) {
  const [isFocused, setIsFocused] = React.useState(false);

  const {
    currency,
    picture,
    url,
    name,
    ordernr: currentOrdernr,
    productId,
    goodId,
  } = getCartProductInfo(product);
  const {
    unit,
    storesInfo,
    sumPrice,
    sumPriceWithoutVat,
    finalPricePerUnitWithVat,
    finalPricePerUnitWithoutVat,
    wholePackagesOnly,
    defaultPackageSize,
    boxQuantity,
    price,
    isSale,
    isSaleB2B,
    isSaleout,
    oldPrice,
  } = getZemplinCartProductInfo(product);
  const {
    count: initialCount,
    productPackages,
    onStock,
    onStockExternal,
  } = getZemplinCartProductInfo(product);
  const isMks = unit === 'Mks';

  const isOnSale = isB2B ? isSaleB2B : isSale;
  const goodName = 'good';
  const specialDiscountPcn = prop(product, `${goodName}.special_discount_pcn`);
  const specialDiscount = prop(product, `${goodName}.special_discount`);
  const specialDiscountWithoutVat = prop(
    product,
    `${goodName}.special_discount_without_vat`,
  );
  const alterSpecialDiscount =
    isSaleout && !specialDiscount
      ? parseFloat(price) - parseFloat(oldPrice)
      : 0;
  const abs = Math.abs(alterSpecialDiscount);
  const alterSpecialDiscountWithoutVat = -round((abs / 100) * 80, 2);

  const correctSpecialDiscount = specialDiscount
    ? specialDiscount
    : alterSpecialDiscount;

  const correctSpecialDiscountWithouVat = specialDiscountWithoutVat
    ? specialDiscountWithoutVat
    : alterSpecialDiscountWithoutVat;

  const correctShownSpecialDiscount =
    specialDiscountPcn && specialDiscountPcn !== 0
      ? specialDiscountPcn
      : isB2B
      ? correctSpecialDiscountWithouVat
      : correctSpecialDiscount;
  const correctShownSpecialDiscountText =
    correctShownSpecialDiscount && correctShownSpecialDiscount !== 0
      ? specialDiscountPcn && specialDiscountPcn
        ? `-${correctShownSpecialDiscount}%`
        : `${correctShownSpecialDiscount}${currency}`
      : '';

  // Ak mame kd sipozicii defaultnu hodnotu vlekosti balenia, pouzijeme ju
  const step = defaultPackageSize
    ? defaultPackageSize
    : productPackages
    ? productPackages
    : unit === 'Mks'
    ? 0.001
    : 1;

  const [count, setCount] = React.useState(initialCount.toString());

  const stockSum = onStock + onStockExternal;

  const { color, availText } = getCartProductAvailabilityInfo(
    product,
    isMks ? initialCount * 1000 : initialCount,
    stockSum,
  );
  const correctSumPrice = isB2B
    ? round(+sumPriceWithoutVat, 2)
    : round(+sumPrice, 2);
  const trueAllPrice = isB2B
    ? round(
        isMks
          ? +finalPricePerUnitWithoutVat * 1000
          : +finalPricePerUnitWithoutVat,
        2,
      )
    : round(
        isMks ? +finalPricePerUnitWithVat * 1000 : +finalPricePerUnitWithVat,
        2,
      );
  const correctSumPriceNumber: number = isB2B ? +sumPriceWithoutVat : +sumPrice;
  const correctPerUnitPrice = round(
    count > 1 ? trueAllPrice : correctSumPriceNumber / count,
    2,
  );
  const perUnitPriceString = !isFocused
    ? onStock > 0
      ? formatPriceToString(correctPerUnitPrice, currency)
      : '-'
    : '';
  const priceString =
    onStock > 0 ? formatPriceToString(correctSumPrice, currency) : '-';
  const minAmount = isMks ? 0.001 : 1;

  const trueOnStock = isMks ? stockSum / 1000 : stockSum;

  const handleCountChange = (
    newCount: string,
    shouldAddToCart: boolean = true,
  ) => {
    const withoutCommas = newCount.toString().replace(',', '.');
    let newCountNumber = parseFloat(withoutCommas);

    if (isNaN(newCountNumber)) {
      setCount(newCount);
      return;
    }

    if (shouldAddToCart) {
      if (parseFloat(newCount) < minAmount) {
        newCount = minAmount.toFixed(3);
      }
      newCountNumber = parseFloat(newCount);
      const countDiff = -(+initialCount - +newCountNumber);
      const correctCount = getZemplinTrueCount(unit === 'Mks', countDiff);
      addToCart(product, correctCount);
    }

    setCount(newCount);
  };

  const handleRemoveClick = () => {
    removeFromCart(productId, goodId, false, product);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const handleInputBlur = () => {
    setIsFocused(false);
  };

  return (
    <Wrapper noBorderBottom={hasMinAmountInfo}>
      {(isOnSale || isSaleout) && (
        <DiscountWrapper>
          {isOnSale ? (
            <Tag text={'Akcia'} type={TagType.ACTION} />
          ) : (
            <Tag text={'Výpredaj'} type={TagType.SALEOUT} />
          )}
          {correctShownSpecialDiscountText ? (
            <SaleBubbleNew
              style={{
                minWidth: '16px',
                minHeight: '16px',
                fontSize: '12px',
              }}
              notAbsolute={true}
              text={correctShownSpecialDiscountText}
            />
          ) : null}
        </DiscountWrapper>
      )}
      <UpperWrapper>
        <ImageWrapper to={url}>
          <Image alt={`Obrázok ku produktu ${name}`} src={picture} />
        </ImageWrapper>
        <NameInputWrapper>
          <NameLink to={url}>
            <Name>{name}</Name>
          </NameLink>
          <CustomNrStock
            customNr={currentOrdernr}
            stockReadable={''}
            unit={unit}
            availText={''}
            storesInfo={storesInfo}
          />
        </NameInputWrapper>
      </UpperWrapper>
      <PriceValuesWrapper>
        <MobileTitleValue
          title={isB2B ? __('Cena bez DPH') : __('Cena s DPH')}
          value={perUnitPriceString}
        />
        <MobileTitleValue
          title={isB2B ? __('Spolu bez DPH') : __('Spolu s DPH')}
          value={priceString}
        />
        {productPackages > 0 && (
          <MobileTitleValue
            title={__('Balenie')}
            value={getPackagesString(
              defaultPackageSize ? defaultPackageSize : productPackages,
              unit,
            )}
          />
        )}
        {boxQuantity > 0 && (
          <MobileTitleValue
            title={__('Kartón')}
            value={getPackagesString(boxQuantity, unit)}
          />
        )}
      </PriceValuesWrapper>
      <BottomWrapper>
        <CountSetter
          onChange={handleCountChange}
          unit={unit}
          count={count}
          step={step}
          handleInputFocus={handleInputFocus}
          handleInputBlur={handleInputBlur}
          title={
            (count * 10000) % (step * 10000) !== 0
              ? 'Neobjednávate celé balenie.'
              : null
          }
          backgroundColor={getCountSetterColor(
            onStock,
            isMks,
            step,
            count,
            trueOnStock,
          )}
          defaultPackageSize={defaultPackageSize}
          wholePackagesOnly={wholePackagesOnly}
          onStock={trueOnStock}
        />
        <SvgIcon
          onClick={handleRemoveClick}
          width={16}
          height={16}
          icon={IconType.trash}
          viewBox="0 0 16 16"
          alt="Odstrániť produkt"
          title="Odstrániť produkt"
          fill={theme.colors.textPrimary}
          cursor={'pointer'}
        />
      </BottomWrapper>
      <CustomNrStock
        customNr={''}
        stockReadable={availText}
        textColor={color}
        unit={unit}
        availText={availText}
        storesInfo={storesInfo}
      />
      {prop(product, 'product.backorders.length') > 0 && (
        <StyledLink
          to={__r('routes:moj-ucet/nedodany-tovar', '/moj-ucet/nedodany-tovar')}
        >
          <AlreadyOrderedText>
            {__('Produkt už máte objednaný')}
          </AlreadyOrderedText>
        </StyledLink>
      )}
      {showWholePackagesOnlyWarning(
        onStock,
        isMks,
        count,
        step,
        trueOnStock,
        wholePackagesOnly,
      ) ? (
        <WholePackagesOnly borderBottom={true} />
      ) : null}
      {hasMinAmountInfo ? (
        <MinAmountInfoText
          borderBottom={true}
          isWholePackagesWarningShown={wholePackagesOnly}
        />
      ) : (
        ''
      )}
    </Wrapper>
  );
}

const Wrapper = styled(FlexCol)<{ noBorderBottom: boolean }>`
  width: 100%;
  max-width: ${rem(320)};
  margin: 0 auto;
  padding: ${rem(24)} 0;

  ${({ theme }) => theme.mediab.s450`
      max-width: ${rem(320)};
      width: calc(100vw);
  `}
`;

const UpperWrapper = styled(FlexRow)`
  margin-bottom: ${rem(24)};
`;

const BottomWrapper = styled(FlexRowCenterVertical)`
  margin-bottom: ${rem(16)};
  justify-content: space-between;
  max-width: ${rem(320)};

  ${({ theme }) => theme.mediab.s450`
      max-width: initial;
  `}
`;

const ImageWrapper = styled(Link)`
  margin-right: ${rem(16)};
`;

const Image = styled.img`
  width: ${rem(48)};
  height: ${rem(48)};
  max-width: initial;
`;

const NameInputWrapper = styled(FlexCol)`
  max-width: initial;
`;

const NameLink = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.colors.textPrimary};

  &:hover {
    text-decoration: underline;
  }
`;

const Name = styled(TextP)`
  font-size: ${rem(14)};
  line-height: ${rem(22)};
  margin-bottom: ${rem(4)};

  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: left;
  font-family: ${({ theme }) => theme.font.secondaryLight};
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: ${rem(400)};
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => darken(0.15, theme.colors.primary)};
  }

  ${({ theme }) => theme.mediab.l1300`
      max-width: ${rem(300)};
  `}

  ${({ theme }) => theme.mediab.l1150`
      max-width: ${rem(200)};
  `}
`;

const StyledTableRow = styled(TableRow)`
  padding: ${rem(24)} 0;
  border-top: ${({ theme }) => theme.borders.primary};

  &:last-child {
    border-bottom: ${({ theme }) => theme.borders.primary};
  }
`;

const PriceValuesWrapper = styled(FlexCol)`
  margin-bottom: ${rem(16)};
`;

const DiscountWrapper = styled(FlexRowCenterVertical)`
  gap: ${rem(4)};
  margin-bottom: ${rem(8)};
`;

const WithTheme: any = withTheme(ItemsTabRowMobile);
export { WithTheme as ItemsTabRowMobile, StyledTableRow };
